<template>
  <div class="page-platforms">
    <div class="page-platforms__content">
      <div class="page-admin__table-header">
        <div class="page-platforms__toggle-all">
          <span class="page-platforms__toggle-all-label">{{ $t('page.admin-platforms.toggle-all') }}</span>
          <BaseToggle
            :value="allPlatformsHaveAccess"
            :disabled="pending || !mergedPlatforms.length"
            @change="handleToggleAll"
          />
        </div>
        <div v-if="!isPhone" class="page-admin__actions">
          <BaseRefreshButton
            class="page-admin__refresh"
            :size="40"
            :pending="pending"
            @refresh="refreshTenantPlatforms"
          />
        </div>
      </div>

      <div class="page-platforms__table">
        <TableNormal
          :headers="headers"
          :items="mergedPlatforms"
          :items-per-page="-1"
          class="elevation-1"
          :sort-by="sortBy"
          :sort-desc="sortDesc"
          :loading="pending"
          :loading-text="$t('page.admin-platforms.loading')"
          hide-default-footer
        >
          <!-- Phones: the table draws the count line above its rows (docs/page-layout.md). -->
          <template #phone-count>
            <span class="number-monospace">{{ mergedPlatforms.length }}</span>
            {{ ' ' + $t('shared.platforms') }}
          </template>
          <template #phone-actions>
            <BaseRefreshButton :pending="pending" @refresh="refreshTenantPlatforms" />
          </template>
          <template v-slot:progress>
            <BaseLoadingBar v-if="pending" />
          </template>
          <template v-slot:item.id="{ item }">
            <div class="page-platforms__platform">
              <PlatformIcon :platform="item.id" class="page-platforms__platform-icon" />
              <span class="page-platforms__platform-id">
                {{ getPlatformName(item) }}
              </span>
              <div v-if="item.tags && item.tags.length" class="page-platforms__platform-tags">
                <BaseTag
                  v-for="(tag, index) in item.tags"
                  :key="`${tag}-${index}`"
                  :tone="getPlatformTagTone(tag)"
                  preset="soft"
                  :icon="getPlatformTagIcon(tag)"
                >
                  {{ $t('tags.' + tag) }}
                </BaseTag>
              </div>
            </div>
          </template>

          <template v-slot:item.hasAccess="{ item }">
            <BaseToggle :value="item.hasAccess" @change="handleToggle(item, ...arguments)" />
          </template>
        </TableNormal>
      </div>
    </div>
  </div>
</template>

<script>
import BaseLoadingBar from '@/components/base/BaseLoadingBar';
import BaseRefreshButton from '@/components/base/BaseRefreshButton.vue';
import BaseToggle from '@/components/base/BaseToggle';
import BaseTag from '@/components/base/BaseTag.vue';
import PlatformIcon from '@/components/PlatformIcon';
import TableNormal from '@/components/table/TableNormal.vue';
import { mapActions, mapGetters } from 'vuex';
import { LOCALE } from '@/util/i18n/locale';
import { getPlatformTagTone, getPlatformTagIcon } from '@/util/tag-tones/platform-tags';
import { viewport } from '@/util/viewport';

export default {
  name: 'PageAdminPlatforms',
  components: {
    TableNormal,
    BaseLoadingBar,
    BaseRefreshButton,
    BaseToggle,
    BaseTag,
    PlatformIcon,
  },
  data() {
    return {
      sortBy: ['id'],
      sortDesc: [false],
      headers: [
        { text: this.$t('menu.admin-platforms'), value: 'id' },
        { text: this.$t('page.admin-platforms.tenant-access'), value: 'hasAccess', phone: 'actions' },
      ],
    };
  },
  computed: {
    isPhone() {
      return viewport.size === 'phone';
    },
    ...mapGetters('auth', ['currentUser']),
    ...mapGetters('tenantPlatforms', ['mergedPlatforms', 'pending']),
    allPlatformsHaveAccess() {
      const list = this.mergedPlatforms;
      return list.length > 0 && list.every((p) => p.hasAccess);
    },
  },
  methods: {
    ...mapActions('tenantPlatforms', ['getTenantPlatforms', 'setTenantPlatform', 'setTenantPlatformsBatch']),
    handleToggle(platform, value) {
      this.setTenantPlatform({
        platformAccess: {
          tenantId: this.currentUser.tenantId,
          platformId: platform.platformId,
          hasAccess: value,
        },
      })
        .then(() => {
          this.$toast.success(this.$t('page.admin-platforms.success'));
        })
        .catch((error) => {
          this.$toast.error(error);
        });
    },
    async handleToggleAll(value) {
      const toUpdate = this.mergedPlatforms.filter((p) => p.hasAccess !== value);
      if (!toUpdate.length) {
        return;
      }
      try {
        await this.setTenantPlatformsBatch({
          platformAccessList: toUpdate.map((platform) => ({
            tenantId: this.currentUser.tenantId,
            platformId: platform.platformId,
            hasAccess: value,
          })),
        });
        this.$toast.success(this.$t('page.admin-platforms.success'));
      } catch (error) {
        this.$toast.error(error);
      }
    },
    refreshTenantPlatforms() {
      this.getTenantPlatforms();
    },
    getPlatformName(platform) {
      return this.$i18n.locale === LOCALE.FR_CA ? platform.nameFr : platform.nameEn;
    },
    getPlatformTagTone,
    getPlatformTagIcon,
  },
  async created() {
    await this.getTenantPlatforms();
  },
};
</script>

<style lang="scss">
.page-platforms {
  &__content {
    display: flex;
    flex: auto;
    flex-direction: column;
    padding: 32px;
  }

  &__toggle-all {
    align-items: center;
    display: flex;
    gap: 12px;
  }

  &__toggle-all-label {
    color: $color-normal;
    font-size: 16px;
  }

  &__table {
    .v-data-table.theme--light tbody {
      cursor: default;
    }

    .v-data-table.theme--light tbody td {
      color: $color-normal;
    }
  }

  &__platform {
    display: flex;
    align-items: center;
  }

  &__platform-icon {
    padding-right: 8px;
  }

  &__platform-id {
    font-size: 16px;
    padding-right: 12px;
  }

  &__platform-tags {
    display: flex;
    flex-wrap: wrap;
    align-items: center;
    gap: 4px;
  }

  .v-data-table__progress {
    .base-loading-bar {
      position: absolute;
      top: 48px;
    }
  }
}
</style>
